import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";

const initialState = {
	skills: [],
	skill: null,
	isLoading: false,
	error: null,
};

// Create Skill
export const createSkill = createAsyncThunk("lmsSkill/createSkill", async ({ name, description }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post(`lms/skills`, { name, description });
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// Fetch All Skills
export const fetchSkills = createAsyncThunk("lmsSkill/fetchSkills", async ({ page, limit, filters } = {}, { rejectWithValue }) => {
	try {
		const queryParams = new URLSearchParams({
			page: page ?? 1,
			limit: limit ?? 1000,
			search: filters?.search ?? "",
			includeDeleted: filters?.includeDeleted ? "true" : "",
		});

		const response = await axiosHttp.get(`lms/skills?${queryParams}`);
		return response.data.data.skills || [];
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// Fetch Skill by ID
export const fetchSkillById = createAsyncThunk("lmsSkill/fetchSkillById", async ({ skillId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`lms/skills/${skillId}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// Update Skill
export const updateSkill = createAsyncThunk("lmsSkill/updateSkill", async ({ skillId, name, description }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.put(`lms/skills/${skillId}`, { name, description });
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// Delete Skill (Soft Delete)
export const deleteSkill = createAsyncThunk("lmsSkill/deleteSkill", async ({ skillId }, { rejectWithValue }) => {
	try {
		await axiosHttp.put(`lms/skills/${skillId}/delete`);
		return skillId;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

const skillSlice = createSlice({
	name: "lmsSkill",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Create Skill
			.addCase(createSkill.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(createSkill.fulfilled, (state, action) => {
				state.isLoading = false;
			})
			.addCase(createSkill.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch All Skills
			.addCase(fetchSkills.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchSkills.fulfilled, (state, action) => {
				state.isLoading = false;
				state.skills = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(fetchSkills.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
				state.skills = [];
			})

			// Fetch Skill by ID
			.addCase(fetchSkillById.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchSkillById.fulfilled, (state, action) => {
				state.isLoading = false;
				state.skill = action.payload;
			})
			.addCase(fetchSkillById.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Update Skill
			.addCase(updateSkill.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(updateSkill.fulfilled, (state, action) => {
				state.isLoading = false;
			})
			.addCase(updateSkill.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Delete Skill
			.addCase(deleteSkill.pending, (state) => {
				state.error = null;
			})
			.addCase(deleteSkill.fulfilled, (state, action) => {
				state.error = null;
			})
			.addCase(deleteSkill.rejected, (state, action) => {
				state.error = action.payload;
			});
	},
});

export const { clearError } = skillSlice.actions;
export default skillSlice.reducer;
